import { DEFAULT_CURRENCY, makeCurrencyFormatter, percentageFormatter, scaledPercentageFormatter } from "helpers/formatters/numbers"
import { get, keyBy, merge, map, isEmpty, chain, toLower } from "lodash"
import { generateQueryKey } from "connected-components/dashboard/helpers/query-key"
import { TOP_POSITION_COUNT } from "helpers/analyzeConstants"
import { GMV, REFERENCE_EQUITY, MODEL_COVERAGE, POSITIONS, POSITIONS_COUNT, MAX_CONCENTRATION, MIN_CONCENTRATION, TOP_CONCENTRATION, LONG, SHORT, OVERWEIGHT, UNDERWEIGHT, NET, MARKET_CAP } from "../constants"
import { getMetricValue } from "../composition-helpers"
import { sanitizePositionSet } from "../helpers"
import { getIntradayBatchMoment } from "helpers/intraday"

export const getMetrics = (currency) => {
  const currencyFormatter = makeCurrencyFormatter(currency)
  return {
    [GMV]: {
      label: "GMV",
      path: "gmv",
      formatter: currencyFormatter,
    },
    [REFERENCE_EQUITY]: {
      label: "Reference Equity",
      path: "referenceEquity",
      formatter: currencyFormatter,
    },
    [MODEL_COVERAGE]: {
      label: "Model Coverage",
      path: "coverage.percentGmvAvailable",
      formatter: percentageFormatter,
    },
    [POSITIONS]: {
      label: "Positions",
      path: "positionsCount",
      // Generally there will be a default formatter applied in the renderer but in this case, we specifically want to show the raw number
      // so we have to override it here
      formatter: null,
    },
    [MAX_CONCENTRATION]: {
      label: "Max Concentration",
      path: "concentration.max",
    },
    [MIN_CONCENTRATION]: {
      label: "Min Concentration",
      path: "concentration.min",
    },
    [TOP_CONCENTRATION]: {
      label: `Top ${TOP_POSITION_COUNT} Concentration`,
      path: "concentration.top",
    },
    // Emitted alongside POSITIONS because the overlapping chart splits its axes on this exact key.
    [POSITIONS_COUNT]: {
      label: "Positions",
      path: "positionsCount",
      formatter: null,
    },
    [MARKET_CAP]: {
      label: "Market Capitalization",
      path: "summaryStats.average.marketCapitalization",
      formatter: currencyFormatter,
    },
  }
}

function makeLongShortGetter(metricId) {
  return (item, defaultZero) => {
    const value = chain(item)
      .find(({ id }) => toLower(id) === metricId)
      .get("economicExposure", defaultZero ? 0 : null)
      .value()

    if (value == null) {
      return value
    }
    return Math.abs(value)
  }
}

export const getLongShortMetrics = (isActive) => {
  const metrics = {
    [NET]: {
      label: "Net",
      path: "net",
      formatter: scaledPercentageFormatter,
      valueGetter: (compositionBy, defaultZero) => {
        const long = makeLongShortGetter(isActive ? OVERWEIGHT : LONG)(compositionBy, defaultZero)
        const short = makeLongShortGetter(isActive ? UNDERWEIGHT : SHORT)(compositionBy, defaultZero)
        return long - Math.abs(short)
      },
    },
  }
  if (isActive) {
    metrics[OVERWEIGHT] = {
      label: "Overweight",
      path: "overweight",
      formatter: scaledPercentageFormatter,
      valueGetter: makeLongShortGetter(OVERWEIGHT),
    }
    metrics[UNDERWEIGHT] = {
      label: "Underweight",
      path: "underweight",
      formatter: scaledPercentageFormatter,
      valueGetter: makeLongShortGetter(UNDERWEIGHT),
    }
  } else {
    metrics[LONG] = {
      label: "Long",
      path: "long",
      formatter: scaledPercentageFormatter,
      valueGetter: makeLongShortGetter(LONG),
    }
    metrics[SHORT] = {
      label: "Short",
      path: "short",
      formatter: scaledPercentageFormatter,
      valueGetter: makeLongShortGetter(SHORT),
    }
  }
  return metrics
}

// No live-tick branch exists: ModelIntradaySimulation has no composition field, only this batch type does.
const BATCH_TREND_QUERY = `#multi-date-composition-batch
query MultiDateCompositionBatchQuery($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $on: Date!, $batchId: Id) {
  model(id: $modelId) {
    simulation:intradaySimulationBatch(positionSet: $positionSet, base: $base, on: $on, batchId: $batchId) {
      composition(scaleFormat: PERCENT_EQUITY_GMV) {
        date
        positionsCount
        concentration {
          max
          top(count: ${TOP_POSITION_COUNT})
        }
      }
    }
  }
}`

export function createCompositionIntradayBatchTrendQueries(modelId, positionSet, base, batches) {
  const sanitizedPositionSet = { ...sanitizePositionSet(positionSet), currency: DEFAULT_CURRENCY }

  return batches.map(batch => ({
    key: generateQueryKey("composition.multiDate.batch", { modelId, positionSet: sanitizedPositionSet, base, on: batch.date, batchId: batch.id }),
    skipInvalidation: true,
    query: BATCH_TREND_QUERY,
    variables: { modelId, positionSet: sanitizedPositionSet, base, on: batch.date, batchId: batch.id },
    select: (graphqlResult) => {
      const [row] = get(graphqlResult, "data.model.simulation.composition", [])
      const batchMoment = getIntradayBatchMoment(batch)

      return {
        values: {
          // Drop rather than chart a malformed asOf — an Invalid Date would corrupt sort order.
          dates: row && batchMoment.isValid() ? [{
            date: batchMoment.toISOString(),
            batchId: batch.id,
            [POSITIONS_COUNT]: row.positionsCount,
            [MAX_CONCENTRATION]: get(row, "concentration.max"),
            [TOP_CONCENTRATION]: get(row, "concentration.top"),
          }] : [],
        },
      }
    },
  }))
}

const QUERY = `query compareCompositionPositionSet($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $from: Date!, $to: Date!, $groupBy: ContributorGroupType!) {
  model(id: $modelId) {
    simulation(positionSet: $positionSet, base: $base, from: $from, to: $to, interval: AUTO) {
      coverage {
        date
        coverage: summary  {
          percentGmvAvailable
        }
      }
      longShort: composition(scaleFormat: PERCENT_EQUITY_GMV) {
        date
        compositionBy(groupBy: $groupBy) {
          id
          description
          economicExposure
        }
      }
      composition(scaleFormat: PERCENT_EQUITY_GMV) {
        date
        gmv
        referenceEquity
        positionsCount
        modeledGmv
        summaryStats {
          average {
            marketCapitalization
          }
        }
        concentration {
          min
          max
          top(count: ${TOP_POSITION_COUNT})
        }
      }
    }
  }
}`

export function createCompositionMultiDateQuery(modelId, positionSet, base, from, to, options = {}) {
  const isActive = !isEmpty(base)
  const defaultZeros = options?.defaultZeros || false
  const noGmvOrEquity = options?.noGmvOrEquity || false
  const hasReferenceEquity = options?.hasReferenceEquity || false
  const currency = options?.currency || DEFAULT_CURRENCY

  const longShortMetrics = getLongShortMetrics(isActive)
  const allMetrics = {
    ...getMetrics(currency),
    ...longShortMetrics,
  }

  return {
    key: generateQueryKey("composition.multiDate", { modelId, positionSet, base, from, to }),
    // Composition has no live-tick query, so a price tick has nothing new for this one to report.
    skipInvalidation: true,
    query: QUERY,
    variables: {
      modelId,
      positionSet: {
        ...sanitizePositionSet(positionSet),
        currency,
      },
      base,
      from,
      to,
      groupBy: isActive ? "OVERWEIGHT_UNDERWEIGHT" : "LONG_SHORT",
    },
    select: (graphqlResult) => {
      const coverage = keyBy(get(graphqlResult, "data.model.simulation.coverage", []), "date")
      const beta = keyBy(get(graphqlResult, "data.model.simulation.beta", []), "date")
      const composition = keyBy(get(graphqlResult, "data.model.simulation.composition", []), "date")

      // We want to simplify the longShort data structure to be { [long]: { date: value }, [short]: { date: value }, [net]: { date: value } }
      const longShort = chain(graphqlResult)
        .get("data.model.simulation.longShort", [])
        .keyBy("date")
        .mapValues(({ compositionBy }) =>
          Object.entries(longShortMetrics).reduce((acc, [metricId, { valueGetter }]) => {
            acc[metricId] = valueGetter(compositionBy, defaultZeros)
            return acc
          }, {}),
        )
        .value()

      const all = merge({}, coverage, composition, beta, longShort)

      return {
        values: {
          dates: map(all, (data, date) => {
            const metrics = Object.entries(allMetrics).reduce((acc, [metricId]) => {
              acc[metricId] = getMetricValue(metricId, allMetrics, data, noGmvOrEquity, hasReferenceEquity)
              return acc
            }, {})
            return {
              date,
              ...metrics,
            }
          }),
        },
      }
    },
  }
}
